/**
 * Pure helpers for the booking panel.
 *
 * They live in their own file, importing NOTHING, so a test can reach them
 * without pulling the component in behind them. `add-booking.component.ts`
 * drags the `@beautyapp/users` barrel through its dependency graph, and that
 * barrel reaches `users-upload.component.ts`, whose `import.meta` Jest cannot
 * parse — which is why this component's only spec did not run at all on
 * develop, failing at the import line before a single assertion.
 */

/**
 * Availability can only be checked once we know BOTH the employee AND the
 * concrete date+time window.
 */
export function canCheckAvailability(v: {
  employeeId: string | null;
  bookingDate: string | null;
  startTime: Date | null;
  endTime: Date | null;
}): boolean {
  return !!v.employeeId && !!v.bookingDate && !!v.startTime && !!v.endTime;
}

/**
 * beaut-545. Which specialist the header shows.
 *
 * `loaded` is the employee the form is currently on — it follows the picker,
 * so the header never claims a person the booking is no longer for. The picker
 * only moves when the panel was opened WITHOUT an employee (see
 * {@link isEmployeePickerLocked}); opened from a visit it is read-only, and
 * this simply resolves to that one employee.
 *
 * The fallback exists only for the FIRST PAINT. The opener already hands the
 * whole employee over in `nzData`, so the name can be right immediately
 * instead of appearing a moment later once the lookup resolves. It is scoped
 * to the original id on purpose: used unscoped, a swap would flash the
 * previous person's name while the new one loads, which is worse than the
 * blank it replaces.
 */
export function resolveHeaderEmployee(v: {
  loaded: any;
  currentEmployeeId: string | null;
  originalEmployeeId: string | null;
  originalEmployee: any;
}): any {
  if (v.loaded) return v.loaded;
  if (!v.currentEmployeeId || v.currentEmployeeId !== v.originalEmployeeId) {
    return null;
  }
  return v.originalEmployee ?? null;
}

/**
 * beaut-545. Whether the employee picker in this panel is read-only.
 *
 * A booking carries exactly one employee, and when this panel is opened from
 * the visit planning page that employee is already settled there. Two live
 * pickers for one value is how the header and the saved booking come to
 * disagree, so this one goes read-only.
 *
 * Three cases stay editable, and all are deliberate:
 * - opened with no employee at all — then this picker is where the choice is
 *   made, and locking it would leave no way to make it;
 * - editing an existing booking — a different operation, unchanged here;
 * - a FOLLOW-UP booking: every "+ add procedure" after the first creates a new
 *   booking, which carries its own employee. The page's employee field settles
 *   only the first one, so a later one opens pre-selected but changeable.
 *   Locking it left the operator stuck with whoever's calendar cell they
 *   clicked first.
 */
export function isEmployeePickerLocked(v: {
  employeeId: string | null | undefined;
  booking: unknown;
  followUpBooking?: boolean;
}): boolean {
  return !!v.employeeId && !v.booking && !v.followUpBooking;
}
